import type { FolderContentDto } from './dto/folder-content.dto.ts'
import type { MediaFileUploadUrlDto } from './dto/media-file-upload-url.dto.ts'
import type { MediaFileDataDto } from './dto/media-file.dto.ts'
import { apiClient, apiV3Client } from '../../shared/data/api-client.ts'
import axios from 'axios'
import authApiClient from '../../shared/data/axios.interceptor.ts'
import type { UUID } from '@/features/shared/data/string.types.ts'
import type { MediaCategory } from './dto/media-category.ts'
import type { MediaFolder } from '../domain/models/media-folder.ts'
import { mapMediaFolderDtoToMediaFolder } from '../domain/mappers/map-foldersdto-to-folders.ts'
import pathFromPathChain from '../domain/mappers/path-from-pathchain.ts'
import type { MediaFolderDto } from './dto/media-folder.dto.ts'
import type { BaseDto } from '@/features/shared/data/audiostack/base.dto.ts'

export const MediaFileService = {
  getFolderContent: async (
    folderId: string | undefined = undefined
  ): Promise<FolderContentDto> => {
    const response = await apiV3Client.get<FolderContentDto>( // NOTE: undefined value means root folder
      folderId ? `/folder/${folderId}` : '/folder'
    )
    return response.data
  },

  getCategories: async (): Promise<MediaCategory[]> => {
    interface GetCategoriesResponse {
      fileTypes: Array<{
        name: string
        fileCategories: MediaCategory[]
      }>
    }
    const response = await apiV3Client.get<GetCategoriesResponse>(
      '/file/metadata/file-categories'
    )
    const { fileCategories = [] } =
      response.data.fileTypes.find(({ name }) => name === 'audio') ?? {} // TODO: We only want the "audio" categories for now
    return fileCategories
  },

  getFolderByName: async (
    folderName: string,
    parentFolderId: string | undefined = undefined
  ): Promise<MediaFolder> => {
    const contents = await MediaFileService.getFolderContent(parentFolderId)
    const desired = contents.folders.find(
      ({ folderName: name }) => name === folderName
    )
    if (!desired) {
      throw new Error(`Folder ${folderName} not found`)
    }
    return mapMediaFolderDtoToMediaFolder(
      desired,
      pathFromPathChain(contents.currentPathChain)
    )
  },

  createFolder: async (
    folderName: string,
    parentFolderId?: string
  ): Promise<MediaFolder> => {
    try {
      const response = await apiV3Client.post<MediaFolderDto>('/folder', {
        folderName,
        parentFolderId,
      })
      return mapMediaFolderDtoToMediaFolder(response.data, `./${folderName}`) // TODO: Find a way to handle "pathChahin" for single folders
    } catch (err) {
      // Most probably folder already exists
      return await MediaFileService.getFolderByName(folderName, parentFolderId)
    }
  },

  deleteFolder: async (folderId: string) =>
    await apiV3Client.delete(`/folder/${folderId}`),

  getFileInfo: async (fileId: string): Promise<MediaFileDataDto> => {
    const response = await apiV3Client.get<MediaFileDataDto>(`/file/${fileId}`)
    return response.data
  },

  createUploadMediaFileUrl: async (
    folderId: UUID,
    fileName: string,
    categoryId: UUID,
    categoryName: string
  ): Promise<MediaFileUploadUrlDto> => {
    const response = await apiV3Client.post<MediaFileUploadUrlDto>(
      '/file/create-upload-url',
      {
        folderId,
        fileName,
        categoryId,
        categoryName,
      }
    )
    return response.data
  },

  uploadMediaFile: async (url: string, file: File): Promise<void> => {
    await axios.put(url, file, {
      headers: {
        'Content-Type': file.type,
      },
    })
  },

  patchMediaFile: async (
    id: UUID,
    fileName: string,
    categoryId: UUID,
    categoryName: string
  ): Promise<void> => {
    await apiV3Client.patch(`/file/${id}`, {
      categoryId,
      categoryName,
      fileName,
    })
  },

  deleteMediaFile: async (id: string, folderId: string): Promise<void> => {
    await apiV3Client.delete(`/file/${id}/${folderId}`)
  },

  getFileUrl: async (fileId: string): Promise<string> => {
    const response = await apiV3Client.get<MediaFileDataDto>(`/file/${fileId}`)
    return response.data.url
  },

  fetchFileContents: async (fileURL: string): Promise<Blob> => {
    const response = await apiV3Client.get<Blob>(fileURL, {
      responseType: 'blob',
    })
    return response.data
  },

  downloadMediaFile: async (
    fileUrl: string,
    fileName: string
  ): Promise<void> => {
    const response = await authApiClient.get<Blob>(fileUrl, {
      responseType: 'blob',
    })

    const url = URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  },

  transcribe: async (fileId: string): Promise<string> => {
    const response = await apiClient.post<BaseDto<{ pipelineId: string }>>(
      '/production/suite/transcribe',
      { fileId, language: 'en' }
    )
    return response.data.data.pipelineId
  },

  pipeline: async (pipelineId: string): Promise<string> => {
    const response = await apiClient.get<
      BaseDto<{ results: { data: { text: string } } }>
    >(`/production/suite/pipeline/${pipelineId}`)
    return response.data.data.results.data.text
  },
}
